import {
  Component,
  DestroyRef,
  HostBinding,
  OnInit,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TaxIdValidity } from '@fmnts/common/tax';
import { FmntsButtonModule } from '@fmnts/components/button';
import {
  FmntsMaskedInputModule,
  ValueTransform,
} from '@fmnts/components/masked-input';
import {
  AbstractDeclarativeInputFieldComponent,
  DeclarativeFormsModule,
} from '@fmnts/declarative-forms';
import { FormInputField } from '@fmnts/declarative-forms/core';
import { I18nModule } from '@fmnts/i18n';
import { EMPTY, Observable, map, merge } from 'rxjs';
import { hasTaxIdError } from '../tax-validation-builder.service';

type FieldValue = string;

/**
 * Component that renders an input field for tax IDs.
 *
 * // TODO(form-field): Use fmnts-form-field once it is implemented.
 * Currently, the styling is done via next styles. Once the component
 * is available, the styling should be done via this.
 */
@Component({
  selector: 'declarative-tax-input-field',
  templateUrl: './tax-input-field.component.html',
  standalone: true,
  imports: [
    DeclarativeFormsModule,
    FmntsButtonModule,
    FmntsMaskedInputModule,
    FormsModule,
    I18nModule,
    ReactiveFormsModule,
  ],
})
export class DeclarativeTaxInputFieldComponent
  extends AbstractDeclarativeInputFieldComponent<FormInputField, FieldValue>
  implements OnInit
{
  private readonly destroyRef = inject(DestroyRef);
  @HostBinding('class') componentClass = 'form-field form-field__text';

  /**
   * Value for the `autocomplete` attribute of the input field.
   */
  readonly autocomplete = input(this._getAutocompleteBehavior());

  get value(): FieldValue {
    return this._value;
  }

  set value(value: FieldValue) {
    this.changeValue(value);
    this._onTouched();
  }
  private _value: FieldValue = '';

  /**
   * Country for which the tax number is specified.
   */
  protected region: string | null = null;

  protected _displayValueTransformer:
    | ValueTransform<string, string>
    | undefined = undefined;
  protected autocapitalize: string | null = null;
  protected inputmode: string | null = null;

  ngOnInit(): void {
    // At the moment it is not considered that the region can
    // change dynamically.
    this.region = this.field.attrs?.country as string;
    this._setupValuesBasedOnRegion(this.region);

    // Sets up listener for changes on the form that might need to retrigger a re-validation for this control
    this._needsValidityUpdate$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.control?.updateValueAndValidity();
      });
  }

  /**
   * Changes the value and calls the onChange listeners.
   *
   * @param value The new value
   */
  changeValue(value: string): void {
    this._value = value;
    this._onChange(value);
  }

  writeValue(value: string): void {
    this._value = value;
  }

  get showError(): boolean {
    return (
      this.form &&
      (!!this.value || this.control.touched) &&
      this.control.invalid
    );
  }

  /**
   * Checks whether all validation errors can be dismissed
   * @returns
   * `true` if the error can be dismissed
   */
  protected canDismissError(): boolean {
    // Allow to dismiss errors if the `taxId` error is the one and
    // only error on this control
    const errors = this.control.errors;
    if (Object.keys(errors ?? {}).length !== 1) {
      return false;
    }

    if (!hasTaxIdError(errors)) {
      return false;
    }

    // Dismissing might be region / validity-dependent
    return (
      this.region === 'it' &&
      errors.taxId.reason === TaxIdValidity.MismatchAdditionalData
    );
  }

  /**
   * Dismisses all validation errors on the current control
   */
  protected dismissAllErrors(): void {
    // Make sure that we don't deal with any async validation by
    // checking again for invalid here.
    if (!this.control.invalid) {
      return;
    }

    this.control.setErrors(null);
  }

  private _setupValuesBasedOnRegion(region: string) {
    switch (region) {
      case 'it': {
        this._displayValueTransformer = {
          transform: (value) => (value ?? '').toUpperCase(),
          reverse: (value) => (value ?? '').toUpperCase(),
        };
        this.autocapitalize = 'characters';
        this.inputmode = 'text';
      }
    }
  }

  /**
   * @returns
   * An `Observable` that emits in case that the control needs an
   * update for the validity.
   */
  private _needsValidityUpdate$(): Observable<void> {
    switch (this.region) {
      case 'it':
        // TODO: Find another way to retrigger validation inside the Tax Validation Builder
        return merge(
          ...[
            ['personalDataKeys', 'date_of_birth'],
            ['personalDataKeys', 'first_name'],
            ['personalDataKeys', 'last_name'],
          ].map((path) => this.form.get(path)?.valueChanges ?? EMPTY),
        ).pipe(map(() => {}));
      default:
        return EMPTY;
    }
  }
}
